'use client';

import { useState, useRef, useEffect, Suspense } from 'react';
import { signIn, useSession } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { Loader2, ArrowLeft, Phone, KeyRound, User, ChevronDown } from 'lucide-react';
import { useTurnstile } from '@/lib/useTurnstile';

const API_URL = `${process.env.NEXT_PUBLIC_BACKEND_URL!}/api/v1/consumer`;

// Common country codes
const countryCodes = [
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
];

type Step = 'details' | 'otp';

export default function SignupPage() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>}>
      <SignupPageContent />
    </Suspense>
  );
}

function SignupPageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { status } = useSession();
  const isNewUser = searchParams.get('new_user') === '1';

  // If an already-logged-in user opens an invite link (…/signup?referral=CODE),
  // signup is irrelevant — send them straight to their profile.
  useEffect(() => {
    if (status === 'authenticated') router.replace('/account');
  }, [status, router]);

  const [step, setStep] = useState<Step>('details');
  const [name, setName] = useState('');
  const [countryCode, setCountryCode] = useState(searchParams.get('countryCode') || '+91');
  const [phone, setPhone] = useState(searchParams.get('phone') || '');
  const [referralCode, setReferralCode] = useState(
    // `referral` is the dedicated referral-code param. We intentionally do NOT
    // read `ref` here: that key is used for marketing/source tags
    // (e.g. ?ref=topnav, ?ref=search) and is persisted by useUTMPersistence,
    // so reading it would prefill a bogus "referral code".
    (searchParams.get('referral') || '').toUpperCase()
  );
  const [otp, setOtp] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const inFlightRef = useRef(false);
  const { getToken, containerRef, isVerifying } = useTurnstile();

  // Handle details submission
  const handleDetailsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inFlightRef.current || isLoading) return;
    inFlightRef.current = true;
    setError('');
    setIsLoading(true);

    try {
      // First check if user already exists
      const checkResponse = await fetch(`${API_URL}/auth/check-phone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ countryCode, phone }),
      });

      const checkData = await checkResponse.json();

      if (checkData.exists) {
        setError('Phone number already registered. Please login instead.');
        setIsLoading(false);
        inFlightRef.current = false;
        return;
      }

      // Send OTP for signup (attach a Turnstile token; `?? undefined` omits the
      // field when Turnstile is disabled so the optional schema still passes)
      const turnstileToken = await getToken('consumer_signup');
      const response = await fetch(`${API_URL}/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ countryCode, phone, type: 'signup', name, turnstileToken: turnstileToken ?? undefined }),
      });

      const data = await response.json();

      if (!response.ok) {
        // A 403 is the bot check, not rate limiting — retryable immediately.
        if (response.status === 403) {
          setError('We couldn\'t verify your browser. Please try again.');
          return;
        }
        setError(
          data.error ||
            'We couldn\'t send the OTP right now. Please check your number or try again in a moment.'
        );
        startCooldownTimer(data.retryAfter || 15);
        return;
      }

      setAuthToken(data.authToken);
      setStep('otp');
      startCooldownTimer(60); // 60 second cooldown for resend
    } catch (err) {
      setError(
        'We couldn\'t reach our servers. Check your internet connection and try again.'
      );
      startCooldownTimer(10);
      console.error('Send OTP error:', err);
    } finally {
      setIsLoading(false);
      inFlightRef.current = false;
    }
  };

  // Handle OTP verification and signup
  const handleOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inFlightRef.current || isLoading) return;
    inFlightRef.current = true;
    setError('');
    setIsLoading(true);

    try {
      const result = await signIn('otp-signup', {
        authToken,
        otp,
        name,
        referralCode: referralCode.trim().toUpperCase(),
        redirect: false,
      });

      if (result?.error) {
        setError('Invalid OTP. Please try again.');
        return;
      }

      if (result?.ok) {
        router.push('/');
        router.refresh();
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error('Signup error:', err);
    } finally {
      setIsLoading(false);
      inFlightRef.current = false;
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (resendCooldown > 0 || inFlightRef.current || isLoading) return;
    inFlightRef.current = true;
    setError('');
    setIsLoading(true);

    try {
      const turnstileToken = await getToken('consumer_signup');
      const response = await fetch(`${API_URL}/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ countryCode, phone, type: 'signup', name, turnstileToken: turnstileToken ?? undefined }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 403) {
          setError('We couldn\'t verify your browser. Please try again.');
          return;
        }
        setError(data.error || 'We couldn\'t resend the OTP. Please try again shortly.');
        startCooldownTimer(data.retryAfter || 15);
        return;
      }

      setAuthToken(data.authToken);
      startCooldownTimer(60);
    } catch (err) {
      setError('Failed to resend OTP. Please check your connection and try again.');
      startCooldownTimer(10);
    } finally {
      setIsLoading(false);
      inFlightRef.current = false;
    }
  };

  // Cooldown timer
  const startCooldownTimer = (seconds: number) => {
    setResendCooldown(seconds);
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Go back to details step
  const handleBack = () => {
    setStep('details');
    setOtp('');
    setError('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-indigo-100 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900">
              {isNewUser ? 'You look like a new user!' : 'Create Account'}
            </h1>
            <p className="text-gray-600 mt-2">
              {step === 'details'
                ? isNewUser
                  ? 'Please enter your details to get registered'
                  : 'Enter your details to get started'
                : 'Enter the OTP sent to your WhatsApp'}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Cloudflare Turnstile. Mounted once here (not inside a step form) so
              it survives the details -> otp step change that resend relies on.
              Never display:none — that breaks the widget's iframe; with
              appearance:'interaction-only' it is zero-height until a challenge
              is shown, and the submit button hides while it runs. */}
          <div className={isVerifying ? 'mb-6 flex min-h-12 items-center justify-center' : ''}>
            <div ref={containerRef} />
          </div>

          {/* Details Step */}
          {step === 'details' && (
            <form onSubmit={handleDetailsSubmit} className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Full Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full h-11 pl-10 pr-4 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    minLength={2}
                    maxLength={100}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                <div className="flex gap-2">
                  <div className="relative">
                    <select
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      className="appearance-none w-[110px] h-11 px-3 pr-8 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {countryCodes.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.flag} {c.code}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      id="phone"
                      type="tel"
                      placeholder="9876543210"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                      className="w-full h-11 pl-10 pr-4 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      maxLength={15}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="referralCode" className="block text-sm font-medium text-gray-700">
                  Referral code <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  id="referralCode"
                  type="text"
                  placeholder="Enter code to get ₹100 off"
                  value={referralCode}
                  onChange={(e) =>
                    setReferralCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16))
                  }
                  className="w-full h-11 px-4 border border-gray-300 rounded-lg text-sm tracking-widest uppercase focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  maxLength={16}
                />
              </div>

              {!isVerifying && (
                <button
                  type="submit"
                  className="w-full h-11 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  disabled={isLoading || name.length < 2 || phone.length < 6 || resendCooldown > 0}
                  aria-busy={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending OTP...
                    </>
                  ) : resendCooldown > 0 ? (
                    `Try again in ${resendCooldown}s`
                  ) : (
                    'Send OTP via WhatsApp'
                  )}
                </button>
              )}
            </form>
          )}

          {/* OTP Step */}
          {step === 'otp' && (
            <form onSubmit={handleOTPSubmit} className="space-y-6">
              <button
                type="button"
                onClick={handleBack}
                className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Edit details
              </button>

              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Name:</span> {name}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Phone:</span> {countryCode} {phone}
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700">
                  Enter OTP
                </label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    id="otp"
                    type="text"
                    inputMode="numeric"
                    placeholder="123456"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    className="w-full h-11 pl-10 pr-4 border border-gray-300 rounded-lg text-center text-lg tracking-widest focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    maxLength={6}
                    required
                  />
                </div>
                <p className="text-xs text-gray-500">
                  OTP sent to WhatsApp: {countryCode} {phone}
                </p>
              </div>

              <button
                type="submit"
                className="w-full h-11 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                disabled={isLoading || otp.length !== 6}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Verify & Create Account'
                )}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={resendCooldown > 0 || isLoading}
                  className="text-sm text-purple-600 hover:text-purple-700 disabled:text-gray-400"
                >
                  {resendCooldown > 0
                    ? `Resend OTP in ${resendCooldown}s`
                    : 'Resend OTP'}
                </button>
              </div>
            </form>
          )}

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link
                href="/login"
                className="text-purple-600 hover:text-purple-700 font-medium"
              >
                Login
              </Link>
            </p>
          </div>
        </div>

        {/* Terms */}
        <p className="text-center text-xs text-gray-500 mt-4">
          By signing up, you agree to our{' '}
          <Link href="/terms-of-services" className="underline">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link href="/privacy-policy" className="underline">
            Privacy Policy
          </Link>
        </p>
      </div>
    </div>
  );
}
